import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { 
  Map, Warehouse, Package, Beef, MapPin, Calendar, Cloud, Brain, Bot, Leaf,
  Calculator, Scale, Euro, Users, Target, Truck, ShoppingBag, Settings,
  GripVertical, Landmark, Save, RotateCcw, FileText, Sun, CloudRain, 
  CloudSnow, Wind, Thermometer, StickyNote, X, Plus, Edit2, Check,
  Tractor, Droplets, Flame, Wrench, Phone, Mail, CreditCard, BarChart3,
  ClipboardList, Sprout, Building, HelpCircle, Zap, Star, Trash2, Newspaper,
  PawPrint, Wine, Bell, AlertTriangle, Clock, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ActualitesWidget } from '@/components/ActualitesWidget';
import MesTachesWidget from '@/components/MesTachesWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images de dashboard
const DASHBOARD_IMAGES = {
  elsa: "https://static.prod-images.emergentagent.com/jobs/6c0de4ab-3306-4e00-aa25-797a555bcec8/images/96999ff00c5bd56f2abd66c3828bddeb1eed27217dc504e098824dc01660507c.png",
  default: "https://static.prod-images.emergentagent.com/jobs/d20cff0f-4829-4bc1-8d94-3a009698c51c/images/f52873e7f110a1c33e88697609c8eedc2cb22aa2728d60b0c832748249fe8ef7.png"
};

// Configuration COMPLÈTE des modules disponibles
const ALL_MODULES = {
  // Exploitation
  parcellaire: { icon: Map, label: 'Parcellaire', route: '/parcellaire', category: 'exploitation' },
  interventions: { icon: Tractor, label: 'Interventions', route: '/interventions', category: 'exploitation' },
  stocks: { icon: Package, label: 'Stocks', route: '/stocks', category: 'exploitation' },
  materiel: { icon: Wrench, label: 'Matériel', route: '/materiel', category: 'exploitation' },
  irrigation: { icon: Droplets, label: 'Irrigation', route: '/parcellaire', category: 'exploitation' },
  elevage: { icon: Beef, label: 'Élevage', route: '/elevage', category: 'exploitation' },
  planning: { icon: Calendar, label: 'Planning', route: '/planning', category: 'exploitation' },
  meteo: { icon: Cloud, label: 'Météo', route: '/meteo', category: 'exploitation' },
  cave: { icon: Wine, label: 'Cave', route: '/cave', category: 'exploitation' },
  hangar: { icon: Warehouse, label: 'Hangar', route: '/materiel', category: 'exploitation' },
  carte: { icon: MapPin, label: 'Carte', route: '/parcellaire', category: 'exploitation' },
  
  // Commercial / CRM
  crm: { icon: Users, label: 'CRM', route: '/crm', category: 'commercial' },
  clients: { icon: Users, label: 'Clients', route: '/crm', category: 'commercial' },
  ventes: { icon: Euro, label: 'Ventes', route: '/crm', category: 'commercial' },
  devis: { icon: FileText, label: 'Devis', route: '/crm', category: 'commercial' },
  factures: { icon: CreditCard, label: 'Factures', route: '/comptabilite', category: 'commercial' },
  fournisseurs: { icon: Truck, label: 'Fournisseurs', route: '/crm', category: 'commercial' },
  
  // Finance / Compta
  comptabilite: { icon: Calculator, label: 'Comptabilité', route: '/comptabilite', category: 'finance' },
  banque: { icon: Landmark, label: 'Banque', route: '/finance', category: 'finance' },
  tresorerie: { icon: BarChart3, label: 'Trésorerie', route: '/finance', category: 'finance' },
  
  // Réglementaire
  reglementaire: { icon: Scale, label: 'Réglementaire', route: '/reglementaire', category: 'reglementaire' },
  carbone: { icon: Flame, label: 'Carbone', route: '/reglementaire', category: 'reglementaire' },
  documents: { icon: FileText, label: 'Documents', route: '/reglementaire', category: 'reglementaire' },
  
  // IA & Outils
  cerveau: { icon: Brain, label: 'Mon Cerveau', route: '/brain', category: 'ia' },
  agents: { icon: Bot, label: 'Équipe IA', route: '/agents', category: 'ia' },
  casia: { icon: Leaf, label: 'CASIA', route: '/agent-casia', category: 'ia' },
  assistante: { icon: Zap, label: 'Assistante', route: '/assistante', category: 'ia' },
  
  // Élevage
  elevage: { icon: PawPrint, label: 'Élevage', route: '/elevage', category: 'exploitation' },
  chevaux: { icon: PawPrint, label: 'Chevaux', route: '/elevage/chevaux', category: 'exploitation' },
  
  // Marketing
  studioIA: { icon: Star, label: 'Studio IA', route: '/studio-ia', category: 'marketing' },
  ecommerce: { icon: ShoppingBag, label: 'E-commerce', route: '/ecommerce', category: 'marketing' },
  
  // RH
  equipe: { icon: Users, label: 'Équipe', route: '/team', category: 'rh' },
  rhSocial: { icon: Building, label: 'RH Social', route: '/rh-social', category: 'rh' },
  
  // Divers
  aide: { icon: HelpCircle, label: 'Aide', route: '/aide', category: 'divers' },
  parametres: { icon: Settings, label: 'Paramètres', route: '/settings', category: 'divers' },
};

// Layout par défaut - sans Élevage ni CASIA pour tous
const DEFAULT_LAYOUT = {
  etagereHaute: ['parcellaire', 'materiel', 'stocks'],
  etagereBasse: ['interventions', 'irrigation', 'planning', 'meteo'],
  tiroirsGauche: ['comptabilite', 'reglementaire', 'documents', 'carbone'],
  tiroirsDroite: ['ventes', 'clients', 'devis', 'fournisseurs'],
  postitsGauche: ['planning', 'cerveau'],
  ecran: ['cerveau', 'agents', 'stocks', 'banque'],
};

// Catégories pour le panneau de personnalisation
const CATEGORIES = {
  exploitation: { label: 'Exploitation', color: 'bg-green-500' },
  commercial: { label: 'Commercial', color: 'bg-blue-500' },
  finance: { label: 'Finance', color: 'bg-purple-500' },
  reglementaire: { label: 'Réglementaire', color: 'bg-amber-500' },
  ia: { label: 'IA & Outils', color: 'bg-pink-500' },
  marketing: { label: 'Marketing', color: 'bg-orange-500' },
  rh: { label: 'RH', color: 'bg-cyan-500' },
  divers: { label: 'Divers', color: 'bg-gray-500' },
};

// Zone cliquable transparente
const ClickZone = ({ moduleKey, onClick, style, testId }) => {
  const mod = ALL_MODULES[moduleKey];
  if (!mod) return null;
  
  return (
    <button
      onClick={() => onClick(mod.route)}
      className="absolute hover:bg-white/10 active:bg-white/20 transition-colors cursor-pointer rounded"
      style={style}
      data-testid={testId}
      title={mod.label}
    />
  );
};

// Tiroir cliquable avec étiquette visible
const DrawerZone = ({ moduleKey, onClick, onRemove, editMode }) => {
  const module = ALL_MODULES[moduleKey];
  if (!module) return null;
  const Icon = module.icon;
  
  return (
    <div className="relative w-full h-full group">
      <button
        onClick={() => !editMode && onClick(module.route)}
        className="w-full h-full bg-amber-800/40 hover:bg-amber-600/50 active:bg-amber-500/60 border border-amber-900/30 transition-all cursor-pointer flex items-center gap-2 px-3 rounded-sm shadow-inner"
        data-testid={`drawer-${moduleKey}`}
        title={module.label}
      >
        <Icon className="w-4 h-4 text-amber-100/90" />
        <span className="text-xs text-amber-100 font-medium truncate">{module.label}</span>
      </button>
      {editMode && (
        <button
          onClick={() => onRemove(moduleKey)}
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Post-it stylisé
const PostIt = ({ moduleKey, onClick, onRemove, editMode, color = 'yellow' }) => {
  const module = ALL_MODULES[moduleKey];
  if (!module) return null;
  const Icon = module.icon;
  
  const colors = {
    yellow: 'from-yellow-200 to-yellow-300 text-yellow-800 shadow-yellow-400/30',
    blue: 'from-sky-200 to-sky-300 text-sky-800 shadow-sky-400/30',
    green: 'from-green-200 to-green-300 text-green-800 shadow-green-400/30',
    pink: 'from-pink-200 to-pink-300 text-pink-800 shadow-pink-400/30',
  };
  
  return (
    <div className="relative group">
      <button
        onClick={() => !editMode && onClick(module.route)}
        className={`
          w-14 h-14 sm:w-16 sm:h-16
          bg-gradient-to-br ${colors[color]}
          rounded shadow-lg hover:shadow-xl hover:scale-105 active:scale-95
          transition-all duration-200
          flex flex-col items-center justify-center gap-0.5
          ${editMode ? 'animate-pulse' : ''}
        `}
        data-testid={`postit-${moduleKey}`}
      >
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        <span className="text-[7px] sm:text-[8px] font-bold uppercase">{module.label}</span>
      </button>
      {editMode && (
        <button
          onClick={() => onRemove(moduleKey)}
          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// Icône d'écran
const ScreenIcon = ({ moduleKey, onClick, onRemove, editMode }) => {
  const module = ALL_MODULES[moduleKey];
  if (!module) return null;
  const Icon = module.icon;
  
  return (
    <div className="relative group">
      <button
        onClick={() => !editMode && onClick(module.route)}
        className={`flex flex-col items-center gap-0.5 p-1.5 hover:bg-white/15 rounded transition-all ${editMode ? 'animate-pulse' : ''}`}
        data-testid={`screen-${moduleKey}`}
      >
        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all">
          <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white/90" />
        </div>
        <span className="text-[7px] sm:text-[8px] text-white/70 font-medium">{module.label}</span>
      </button>
      {editMode && (
        <button
          onClick={() => onRemove(moduleKey)}
          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
};

// Widget Météo animé
const MeteoWidget = ({ weather, onClick }) => {
  const getWeatherIcon = () => {
    if (!weather) return <Cloud className="w-8 h-8 text-gray-400" />;
    
    const icone = weather.icone || 'cloudy';
    switch (icone) {
      case 'sun':
        return <Sun className="w-10 h-10 text-yellow-400 animate-pulse" />;
      case 'rain':
        return <CloudRain className="w-10 h-10 text-blue-400" />;
      case 'snow':
        return <CloudSnow className="w-10 h-10 text-blue-200" />;
      case 'partly-cloudy':
        return (
          <div className="relative">
            <Cloud className="w-10 h-10 text-gray-300" />
            <Sun className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1" />
          </div>
        );
      default:
        return <Cloud className="w-10 h-10 text-gray-400" />;
    }
  };

  return (
    <button
      onClick={onClick}
      className="bg-gradient-to-br from-sky-400/90 to-blue-500/90 backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-white min-w-[120px]"
      data-testid="meteo-widget"
    >
      <div className="flex items-center gap-2">
        {getWeatherIcon()}
        <div className="text-left">
          <p className="text-2xl font-bold">{weather?.temperature || '--'}°C</p>
          <p className="text-xs opacity-90">{weather?.description || 'Chargement...'}</p>
          {weather?.vent_kmh && (
            <p className="text-[10px] opacity-75 flex items-center gap-1">
              <Wind className="w-3 h-3" /> {weather.vent_kmh} km/h
            </p>
          )}
        </div>
      </div>
    </button>
  );
};

// Widget Notes avec titre personnalisable
const NotesWidget = ({ notes, onSave, isEditing, setIsEditing, title, onTitleChange }) => {
  const [localNotes, setLocalNotes] = useState(notes || '');
  const [editingTitle, setEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(title || 'MES NOTES');

  useEffect(() => {
    setLocalNotes(notes || '');
  }, [notes]);

  useEffect(() => {
    setLocalTitle(title || 'MES NOTES');
  }, [title]);

  const handleSave = () => {
    onSave(localNotes);
    setIsEditing(false);
  };

  const handleSaveTitle = () => {
    onTitleChange && onTitleChange(localTitle);
    setEditingTitle(false);
  };

  return (
    <div 
      className="bg-gradient-to-br from-amber-100 to-yellow-200 rounded-lg shadow-xl p-3 w-48 sm:w-56 transform rotate-1 hover:rotate-0 transition-transform"
      data-testid="notes-widget"
    >
      <div className="flex items-center justify-between mb-2 border-b border-amber-300/50 pb-1">
        <div className="flex items-center gap-1 text-amber-800">
          <StickyNote className="w-4 h-4" />
          {editingTitle ? (
            <input
              type="text"
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onBlur={handleSaveTitle}
              onKeyPress={(e) => e.key === 'Enter' && handleSaveTitle()}
              className="text-xs font-bold bg-transparent border-b border-amber-600 focus:outline-none w-24"
              autoFocus
            />
          ) : (
            <span 
              className="text-xs font-bold cursor-pointer hover:text-amber-600"
              onDoubleClick={() => setEditingTitle(true)}
              title="Double-cliquez pour modifier le titre"
            >
              {localTitle}
            </span>
          )}
        </div>
        {isEditing ? (
          <button onClick={handleSave} className="p-1 hover:bg-amber-300/50 rounded">
            <Check className="w-4 h-4 text-green-700" />
          </button>
        ) : (
          <button onClick={() => setIsEditing(true)} className="p-1 hover:bg-amber-300/50 rounded">
            <Edit2 className="w-4 h-4 text-amber-700" />
          </button>
        )}
      </div>
      
      {isEditing ? (
        <textarea
          value={localNotes}
          onChange={(e) => setLocalNotes(e.target.value)}
          className="w-full h-28 text-xs bg-transparent border-none resize-none focus:outline-none text-amber-900 placeholder-amber-600/50"
          placeholder="Écrivez vos notes ici..."
          autoFocus
        />
      ) : (
        <div 
          className="h-28 text-xs text-amber-900 overflow-auto cursor-pointer whitespace-pre-wrap"
          onClick={() => setIsEditing(true)}
        >
          {localNotes || <span className="text-amber-600/50 italic">Cliquez pour ajouter des notes...</span>}
        </div>
      )}
    </div>
  );
};

// Post-it "À ne pas oublier" avec titre personnalisable
const TasksPostIt = ({ tasks, title, onTitleChange }) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(title || 'À NE PAS OUBLIER');

  useEffect(() => {
    setLocalTitle(title || 'À NE PAS OUBLIER');
  }, [title]);

  const handleSaveTitle = () => {
    onTitleChange && onTitleChange(localTitle);
    setEditingTitle(false);
  };

  return (
    <div 
      className="bg-gradient-to-br from-pink-100 to-rose-200 text-pink-800 rounded-lg shadow-xl p-3 w-44 sm:w-52 transform -rotate-1 hover:rotate-0 transition-transform"
      data-testid="tasks-postit"
    >
      <div className="flex items-center gap-1 mb-2 border-b border-current/20 pb-1">
        <FileText className="w-4 h-4" />
        {editingTitle ? (
          <input
            type="text"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={handleSaveTitle}
            onKeyPress={(e) => e.key === 'Enter' && handleSaveTitle()}
            className="text-xs font-bold bg-transparent border-b border-pink-600 focus:outline-none w-28"
            autoFocus
          />
        ) : (
          <span 
            className="text-xs font-bold cursor-pointer hover:text-pink-600"
            onDoubleClick={() => setEditingTitle(true)}
            title="Double-cliquez pour modifier le titre"
          >
            {localTitle}
          </span>
        )}
      </div>
      <ul className="space-y-1 text-xs">
        {tasks.map((task, idx) => (
          <li key={idx} className="flex items-start gap-1">
            <span className="mt-0.5">•</span>
            <span className="line-clamp-2">{task}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Widget Briefing IA
const BriefingWidget = ({ onClick }) => {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchBriefing = async () => {
      try {
        const token = localStorage.getItem('token');
        const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
        if (!token) return;
        
        const headers = { 'Authorization': `Bearer ${token}` };
        if (societeId) headers['X-Societe-Id'] = societeId;
        
        const response = await fetch(`${API_URL}/api/dashboard/briefing`, { headers });
        
        if (response.ok) {
          const data = await response.json();
          setBriefing(data);
        }
      } catch (error) {
        console.log('Briefing non disponible');
      } finally {
        setLoading(false);
      }
    };

    fetchBriefing();
    const interval = setInterval(fetchBriefing, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const hasProspects = briefing?.prospects_chauds?.length > 0;
  const hasRappels = briefing?.rappels_jour?.length > 0;
  const hasNotifs = briefing?.notifs_non_lues > 0;
  const hasActivity = hasProspects || hasRappels || hasNotifs;

  return (
    <div className="relative" data-testid="briefing-widget">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`
          ${hasActivity ? 'bg-gradient-to-br from-indigo-500/90 to-violet-600/90' : 'bg-gradient-to-br from-slate-600/90 to-slate-700/90'}
          backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-white min-w-[160px] text-left
        `}
      >
        <div className="flex items-center gap-2 mb-1">
          <Zap className={`w-5 h-5 ${hasActivity ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-bold tracking-wide">BRIEFING IA</span>
          {hasActivity && (
            <span className="ml-auto w-2 h-2 rounded-full bg-yellow-400 animate-ping" />
          )}
        </div>
        
        {loading ? (
          <p className="text-xs opacity-75">Chargement...</p>
        ) : (
          <div className="space-y-0.5">
            {hasRappels && (
              <div className="flex items-center gap-1 text-xs">
                <Clock className="w-3 h-3 shrink-0" />
                <span>{briefing.rappels_jour.length} rappel{briefing.rappels_jour.length > 1 ? 's' : ''}</span>
              </div>
            )}
            {hasProspects && (
              <div className="flex items-center gap-1 text-xs">
                <Target className="w-3 h-3 shrink-0 text-yellow-300" />
                <span className="font-semibold">{briefing.prospects_chauds.length} prospect{briefing.prospects_chauds.length > 1 ? 's' : ''} chaud{briefing.prospects_chauds.length > 1 ? 's' : ''}</span>
              </div>
            )}
            {hasNotifs && (
              <div className="flex items-center gap-1 text-xs">
                <Bell className="w-3 h-3 shrink-0" />
                <span>{briefing.notifs_non_lues} notif{briefing.notifs_non_lues > 1 ? 's' : ''}</span>
              </div>
            )}
            {briefing?.emails_semaine > 0 && (
              <div className="flex items-center gap-1 text-[10px] opacity-75">
                <Mail className="w-3 h-3 shrink-0" />
                <span>{briefing.emails_semaine} emails (7j)</span>
              </div>
            )}
            {!hasActivity && !loading && (
              <p className="text-xs opacity-90">RAS - Bonne journee</p>
            )}
          </div>
        )}
      </button>

      {expanded && briefing && (
        <div className="absolute top-full mt-2 left-0 z-50 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl p-4 w-72 border border-indigo-200 text-gray-800 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-3 border-b pb-2 border-indigo-100">
            <h3 className="text-sm font-bold text-indigo-800 flex items-center gap-1">
              <Zap className="w-4 h-4" /> Briefing du {briefing.date}
            </h3>
            <button onClick={() => setExpanded(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-3.5 h-3.5 text-gray-500" />
            </button>
          </div>

          {hasRappels && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Rappels</p>
              {briefing.rappels_jour.map((r, i) => (
                <p key={i} className="text-xs pl-4 py-0.5 text-gray-700">
                  <span className="font-medium">{r.heure_rappel || '?'}</span> — {r.titre}
                </p>
              ))}
            </div>
          )}

          {hasProspects && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1"><Target className="w-3 h-3" /> Prospects chauds</p>
              {briefing.prospects_chauds.map((p, i) => (
                <p key={i} className="text-xs pl-4 py-0.5 text-gray-700">
                  <span className="font-medium">{p.nom_prospect}</span> — {p.nb_ouvertures} ouvertures
                </p>
              ))}
            </div>
          )}

          {briefing?.campagnes_actives?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Campagnes</p>
              {briefing.campagnes_actives.slice(0, 3).map((c, i) => (
                <p key={i} className="text-xs pl-4 py-0.5 text-gray-700 truncate">
                  {c.objectif?.substring(0, 50)} ({c.pays || 'FR'})
                </p>
              ))}
            </div>
          )}

          {briefing?.emails_semaine > 0 && (
            <div className="mb-2 bg-indigo-50 rounded-lg p-2">
              <p className="text-[10px] font-medium text-indigo-700">
                Prospection 7j : {briefing.emails_semaine} envoyés, {briefing.emails_ouverts_semaine} ouverts 
                ({briefing.emails_semaine > 0 ? Math.round((briefing.emails_ouverts_semaine / briefing.emails_semaine) * 100) : 0}%)
              </p>
            </div>
          )}

          {briefing?.articles_stock_faible?.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Stock faible</p>
              {briefing.articles_stock_faible.slice(0, 3).map((s, i) => (
                <p key={i} className="text-xs pl-4 py-0.5 text-gray-700">
                  {s.nom} : {s.quantite_stock} {s.unite}
                </p>
              ))}
            </div>
          )}

          {briefing?.changements_geofolia?.length > 0 && (
            <div className="mb-2 bg-emerald-50 rounded-lg p-2">
              <p className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1"><Sprout className="w-3 h-3" /> Derniere sync Geofolia</p>
              {briefing.changements_geofolia.map((c, i) => (
                <p key={i} className="text-[10px] text-emerald-600">{c}</p>
              ))}
            </div>
          )}

          {briefing?.alertes_rh?.length > 0 && (
            <div className="mb-2 bg-orange-50 rounded-lg p-2">
              <p className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1"><Users className="w-3 h-3" /> Alertes RH</p>
              {briefing.alertes_rh.slice(0, 3).map((a, i) => (
                <p key={i} className="text-[10px] text-orange-600"><span className="font-medium">{a.nom}</span> : {a.message}</p>
              ))}
            </div>
          )}

          {briefing?.taches_en_cours?.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-purple-700 mb-1 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Taches en cours</p>
              {briefing.taches_en_cours.slice(0, 3).map((t, i) => (
                <p key={i} className="text-[10px] pl-4 text-gray-600"><span className="font-medium">{t.destinataire_nom}</span> : {t.titre?.substring(0, 50)}</p>
              ))}
            </div>
          )}

          {briefing?.reseaux_connectes?.length > 0 && (
            <div className="mb-2 pt-1 border-t border-gray-100">
              <p className="text-[10px] text-gray-500 flex items-center gap-1">
                Reseaux : {briefing.reseaux_connectes.map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(', ')}
              </p>
            </div>
          )}

          {hasNotifs && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="text-[10px] text-gray-500">{briefing.notifs_non_lues} notification{briefing.notifs_non_lues > 1 ? 's' : ''} non lue{briefing.notifs_non_lues > 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Widget Veille Trouvailles IA
const VeilleWidget = () => {
  const [veilles, setVeilles] = useState([]);
  const [nbTrouvailles, setNbTrouvailles] = useState(0);

  useEffect(() => {
    const fetchVeilles = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
        const headers = { 'Authorization': `Bearer ${token}` };
        if (societeId) headers['X-Societe-Id'] = societeId;
        
        const response = await fetch(`${API_URL}/api/veille-materiel/veilles`, { headers });
        if (response.ok) {
          const data = await response.json();
          setVeilles(data.veilles || []);
          setNbTrouvailles(data.veilles?.reduce((acc, v) => acc + (v.nb_resultats_total || 0), 0) || 0);
        }
      } catch (error) {
        console.log('Veilles non disponibles');
      }
    };
    fetchVeilles();
    const interval = setInterval(fetchVeilles, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (veilles.length === 0) return null;

  return (
    <div 
      className="bg-gradient-to-br from-emerald-500/90 to-teal-600/90 backdrop-blur-sm rounded-xl p-3 shadow-lg text-white min-w-[140px]"
      data-testid="veille-widget"
    >
      <div className="flex items-center gap-2 mb-1">
        <Newspaper className="w-5 h-5" />
        <span className="text-xs font-bold">VEILLES ACTIVES</span>
        {nbTrouvailles > 0 && (
          <span className="ml-auto bg-white/20 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{nbTrouvailles}</span>
        )}
      </div>
      <div className="space-y-0.5">
        {veilles.slice(0, 3).map((v, i) => (
          <p key={i} className="text-[10px] opacity-90 truncate">{v.nom} ({v.nb_resultats_total || 0} trouvailles)</p>
        ))}
      </div>
    </div>
  );
};

// Widget Hot Prospects (signaux d'achat via tracking ouverture email)
const HotProspectsWidget = () => {
  const [data, setData] = useState({ hot: [], tiede: [], burst: [], total_envoyes: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchHot = async () => {
      try {
        const token = localStorage.getItem('token');
        const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        if (societeId) headers['X-Societe-Id'] = societeId;
        const res = await fetch(`${API_URL}/api/prospection-tracking/hot-prospects?since_days=14`, { headers });
        if (res.ok) {
          const d = await res.json();
          setData(d);
        }
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    };
    fetchHot();
    const itv = setInterval(fetchHot, 5 * 60 * 1000);
    return () => clearInterval(itv);
  }, []);

  if (loading) return null;
  const burst = data.burst?.length || 0;
  const hot = data.hot?.length || 0;
  const tiede = data.tiede?.length || 0;
  const hasSignal = burst > 0 || hot > 0;

  // Pas de signaux ET pas d'historique → ne pas polluer le dashboard
  if (!hasSignal && tiede === 0 && (data.total_envoyes || 0) === 0) return null;

  return (
    <div className="relative" data-testid="hot-prospects-widget">
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`${burst > 0 ? 'bg-gradient-to-br from-orange-500/95 to-red-600/95 animate-pulse' : hot > 0 ? 'bg-gradient-to-br from-amber-500/90 to-orange-500/90' : 'bg-gradient-to-br from-slate-500/90 to-slate-600/90'} backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-white min-w-[160px] text-left`}
      >
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className={`w-5 h-5 ${burst > 0 ? 'animate-bounce' : ''}`} />
          <span className="text-xs font-bold tracking-wide">SIGNAUX D'ACHAT</span>
        </div>
        <div className="space-y-0.5">
          {burst > 0 && (
            <div className="flex items-center gap-1 text-xs"><Zap className="w-3 h-3" /><span className="font-bold">{burst} burst 24h</span></div>
          )}
          {hot > 0 && (
            <div className="flex items-center gap-1 text-xs"><span>🔥 {hot} chaud{hot > 1 ? 's' : ''}</span></div>
          )}
          {tiede > 0 && (
            <div className="flex items-center gap-1 text-[11px] opacity-90"><span>💧 {tiede} tiède{tiede > 1 ? 's' : ''}</span></div>
          )}
          {!hasSignal && tiede === 0 && (
            <p className="text-[11px] opacity-80">{data.total_envoyes} emails trackés</p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl p-4 w-96 max-h-[70vh] overflow-y-auto border border-orange-200 text-gray-800" data-testid="hot-prospects-panel">
          <div className="flex items-center justify-between mb-3 border-b pb-2">
            <h3 className="text-sm font-bold text-orange-800 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> Signaux d'achat (14j)
            </h3>
            <button onClick={() => setExpanded(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5 text-gray-500" /></button>
          </div>

          {data.burst?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-red-700 mb-1">⚡ BURST 24h ({data.burst.length}) — À appeler aujourd'hui</p>
              {data.burst.slice(0, 5).map((p) => (
                <div key={p.tracking_id} className="text-xs py-1 px-2 mb-1 bg-red-50 rounded" data-testid={`hot-burst-${p.tracking_id}`}>
                  <p className="font-medium truncate">{p.nom_prospect || p.email_prospect}</p>
                  <p className="opacity-75 text-[10px]">⚡ {p.burst_count_24h} ouv./24h · {p.nb_ouvertures} total · « {(p.sujet || '').substring(0, 50)} »</p>
                </div>
              ))}
            </div>
          )}

          {data.hot?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">🔥 Prospects CHAUDS ({data.hot.length}) — ≥3 ouvertures</p>
              {data.hot.slice(0, 8).map((p) => (
                <div key={p.tracking_id} className="text-xs py-1 px-2 mb-1 bg-amber-50 rounded" data-testid={`hot-${p.tracking_id}`}>
                  <p className="font-medium truncate">{p.nom_prospect || p.email_prospect}</p>
                  <p className="opacity-75 text-[10px]">{p.nb_ouvertures} ouvertures · {p.source} · {p.derniere_ouverture ? new Date(p.derniere_ouverture).toLocaleDateString('fr-FR') : '?'}</p>
                </div>
              ))}
            </div>
          )}

          {data.tiede?.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-blue-700 mb-1">💧 Tièdes ({data.tiede.length}) — 2 ouvertures</p>
              {data.tiede.slice(0, 5).map((p) => (
                <div key={p.tracking_id} className="text-xs py-0.5 px-2 mb-0.5 text-gray-700" data-testid={`tiede-${p.tracking_id}`}>
                  • {p.nom_prospect || p.email_prospect} ({p.nb_ouvertures})
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-gray-500 mt-2 text-center">
            {data.total_envoyes || 0} emails trackés · seuil chaud {data.seuils?.hot || 3} ouv.
          </p>
        </div>
      )}
    </div>
  );
};

// Widget Urgences perso (Fred uniquement)
const UrgencesWidget = ({ onClick }) => {
  const [data, setData] = useState({ authorized: false, total: 0, courriers: [], actions: [], emails: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const fetchUrgences = async () => {
    try {
      const token = localStorage.getItem('token');
      const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}` };
      if (societeId) headers['X-Societe-Id'] = societeId;
      const res = await fetch(`${API_URL}/api/dashboard/urgences-fred`, { headers });
      if (res.ok) {
        const d = await res.json();
        setData(d);
      }
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUrgences();
    const itv = setInterval(fetchUrgences, 5 * 60 * 1000);
    return () => clearInterval(itv);
  }, []);

  const markRead = async (type, id) => {
    try {
      const token = localStorage.getItem('token');
      const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (societeId) headers['X-Societe-Id'] = societeId;
      await fetch(`${API_URL}/api/dashboard/urgences-fred/marquer-lu`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ type, id }),
      });
      fetchUrgences();
    } catch (_) {}
  };

  // ⚠ Hook order safe: hooks au-dessus, retours conditionnels en dessous
  if (loading) return null;
  if (!data.authorized) return null;

  const total = data.total || 0;
  const hasUrg = total > 0;

  return (
    <div className="relative" data-testid="urgences-fred-widget">
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`${hasUrg ? 'bg-gradient-to-br from-rose-600/95 to-red-700/95' : 'bg-gradient-to-br from-emerald-600/90 to-green-700/90'} backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-white min-w-[170px] text-left`}
      >
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className={`w-5 h-5 ${hasUrg ? 'animate-bounce' : ''}`} />
          <span className="text-xs font-bold tracking-wide">URGENCES FRED</span>
          {hasUrg && <span className="ml-auto text-xs bg-white/20 rounded px-1.5">{total}</span>}
        </div>
        <div className="space-y-0.5">
          {data.courriers?.length > 0 && (
            <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /><span>{data.courriers.length} courrier{data.courriers.length > 1 ? 's' : ''} urg.</span></div>
          )}
          {data.actions?.length > 0 && (
            <div className="flex items-center gap-1 text-xs"><Clock className="w-3 h-3" /><span>{data.actions.length} action{data.actions.length > 1 ? 's' : ''} CRM</span></div>
          )}
          {data.emails?.length > 0 && (
            <div className="flex items-center gap-1 text-xs"><Mail className="w-3 h-3" /><span>{data.emails.length} email{data.emails.length > 1 ? 's' : ''} prio.</span></div>
          )}
          {!hasUrg && <p className="text-xs opacity-90">Tout est traité ✓</p>}
        </div>
      </button>

      {expanded && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white/95 backdrop-blur-lg rounded-xl shadow-2xl p-4 w-96 max-h-[70vh] overflow-y-auto border border-rose-200 text-gray-800" data-testid="urgences-fred-panel">
          <div className="flex items-center justify-between mb-3 border-b pb-2">
            <h3 className="text-sm font-bold text-rose-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Urgences du jour</h3>
            <button onClick={() => setExpanded(false)} className="p-1 hover:bg-gray-100 rounded"><X className="w-3.5 h-3.5 text-gray-500" /></button>
          </div>

          {data.courriers?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">📬 Courriers ({data.courriers.length})</p>
              {data.courriers.slice(0, 5).map((c) => (
                <div key={c.id} className="text-xs py-1 px-2 mb-1 bg-amber-50 rounded flex items-start gap-2" data-testid={`urg-courrier-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{c.expediteur || 'Inconnu'}</p>
                    <p className="opacity-75 truncate">{c.sujet || c.type_courrier}</p>
                    {c.echeance && <p className="text-[10px] text-rose-600">Échéance : {c.echeance}</p>}
                  </div>
                  <button onClick={() => markRead('courrier', c.id)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`btn-mark-courrier-${c.id}`}>✓ traité</button>
                </div>
              ))}
            </div>
          )}

          {data.actions?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-orange-700 mb-1">🎯 Actions CRM ({data.actions.length})</p>
              {data.actions.slice(0, 5).map((a) => (
                <div key={a.id} className="text-xs py-1 px-2 mb-1 bg-orange-50 rounded flex items-start gap-2" data-testid={`urg-action-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{a.titre}</p>
                    <p className="opacity-75 truncate">{a.client_nom || ''} {a.echeance ? `· ${a.echeance}` : ''}</p>
                  </div>
                  <button onClick={() => markRead('action', a.id)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`btn-mark-action-${a.id}`}>✓ fait</button>
                </div>
              ))}
            </div>
          )}

          {data.emails?.length > 0 && (
            <div className="mb-2">
              <p className="text-xs font-semibold text-indigo-700 mb-1">📨 Emails priorité ({data.emails.length})</p>
              {data.emails.slice(0, 5).map((e) => (
                <div key={e.id} className="text-xs py-1 px-2 mb-1 bg-indigo-50 rounded flex items-start gap-2" data-testid={`urg-email-${e.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{e.objet || '(sans objet)'}</p>
                    <p className="opacity-75 truncate">{e.expediteur} · {e.categorie}</p>
                  </div>
                  <button onClick={() => markRead('email', e.id)} className="text-[10px] text-emerald-700 hover:underline" data-testid={`btn-mark-email-${e.id}`}>✓ lu</button>
                </div>
              ))}
            </div>
          )}

          {total === 0 && <p className="text-xs text-emerald-700 text-center py-3">Tout est sous contrôle 👍</p>}
        </div>
      )}
    </div>
  );
};

// Widget Alertes PAC
const AlertesWidget = ({ onClick }) => {
  const [loading, setLoading] = useState(true);
  const [alertes, setAlertes] = useState({ urgentes: 0, en_retard: 0, prochaines: [] });

  useEffect(() => {
    const fetchAlertes = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/api/alertes/dashboard`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAlertes(data);
        }
      } catch (error) {
        console.log('Alertes non disponibles');
      } finally {
        setLoading(false);
      }
    };

    fetchAlertes();
  }, []);

  const hasUrgent = alertes.urgentes > 0 || alertes.en_retard > 0;

  return (
    <button
      onClick={onClick}
      className={`
        ${hasUrgent ? 'bg-gradient-to-br from-red-500/90 to-orange-500/90 animate-pulse' : 'bg-gradient-to-br from-green-500/90 to-emerald-600/90'}
        backdrop-blur-sm rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all text-white min-w-[140px]
      `}
      data-testid="alertes-widget"
    >
      <div className="flex items-center gap-2 mb-2">
        <Bell className={`w-5 h-5 ${hasUrgent ? 'animate-bounce' : ''}`} />
        <span className="text-xs font-bold">ALERTES PAC</span>
      </div>
      
      {loading ? (
        <p className="text-xs opacity-75">Chargement...</p>
      ) : (
        <div className="space-y-1">
          {alertes.en_retard > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <AlertTriangle className="w-3 h-3" />
              <span className="font-bold">{alertes.en_retard} en retard</span>
            </div>
          )}
          {alertes.urgentes > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <Clock className="w-3 h-3" />
              <span>{alertes.urgentes} urgent{alertes.urgentes > 1 ? 'es' : 'e'}</span>
            </div>
          )}
          {!hasUrgent && (
            <p className="text-xs opacity-90">Tout est à jour ✓</p>
          )}
          {alertes.prochaines?.length > 0 && (
            <p className="text-[10px] opacity-75 mt-1">
              Prochaine: {alertes.prochaines[0]?.titre?.substring(0, 20)}...
            </p>
          )}
        </div>
      )}
    </button>
  );
};

// Panneau de personnalisation
const CustomizationPanel = ({ open, onClose, layout, setLayout, onSave, onReset }) => {
  const [selectedZone, setSelectedZone] = useState('etagereHaute');
  
  const zones = {
    etagereHaute: { label: 'Étagère haute', max: 4 },
    etagereBasse: { label: 'Étagère basse', max: 5 },
    tiroirsGauche: { label: 'Tiroirs gauche', max: 4 },
    tiroirsDroite: { label: 'Tiroirs droite', max: 4 },
    postitsGauche: { label: 'Post-its', max: 3 },
    ecran: { label: 'Écran PC', max: 6 },
  };

  const addModule = (moduleKey) => {
    const zone = zones[selectedZone];
    if (layout[selectedZone].length >= zone.max) {
      toast.error(`Maximum ${zone.max} éléments dans cette zone`);
      return;
    }
    if (layout[selectedZone].includes(moduleKey)) {
      toast.error('Ce module est déjà présent');
      return;
    }
    setLayout(prev => ({
      ...prev,
      [selectedZone]: [...prev[selectedZone], moduleKey]
    }));
  };

  const removeModule = (moduleKey) => {
    setLayout(prev => ({
      ...prev,
      [selectedZone]: prev[selectedZone].filter(m => m !== moduleKey)
    }));
  };

  // Modules utilisés
  const usedModules = Object.values(layout).flat();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Personnaliser mon bureau
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 mt-4">
          {/* Zones */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Zone à modifier</h3>
            <div className="space-y-1">
              {Object.entries(zones).map(([key, { label, max }]) => (
                <button
                  key={key}
                  onClick={() => setSelectedZone(key)}
                  className={`w-full p-2 rounded text-left text-sm flex justify-between items-center ${
                    selectedZone === key ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <span>{label}</span>
                  <Badge variant="secondary" className="text-xs">
                    {layout[key]?.length || 0}/{max}
                  </Badge>
                </button>
              ))}
            </div>

            {/* Contenu actuel de la zone */}
            <h3 className="text-sm font-semibold mt-4 mb-2">Contenu actuel</h3>
            <div className="flex flex-wrap gap-1">
              {layout[selectedZone]?.map((moduleKey) => {
                const mod = ALL_MODULES[moduleKey];
                if (!mod) return null;
                const Icon = mod.icon;
                return (
                  <div
                    key={moduleKey}
                    className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-xs group"
                  >
                    <Icon className="w-3 h-3" />
                    <span>{mod.label}</span>
                    <button
                      onClick={() => removeModule(moduleKey)}
                      className="ml-1 text-red-500 opacity-0 group-hover:opacity-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              {(!layout[selectedZone] || layout[selectedZone].length === 0) && (
                <span className="text-xs text-muted-foreground">Aucun élément</span>
              )}
            </div>
          </div>

          {/* Modules disponibles */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Ajouter un module</h3>
            <ScrollArea className="h-[350px] pr-2">
              {Object.entries(CATEGORIES).map(([catKey, { label, color }]) => {
                const categoryModules = Object.entries(ALL_MODULES).filter(
                  ([_, mod]) => mod.category === catKey
                );
                if (categoryModules.length === 0) return null;

                return (
                  <div key={catKey} className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full ${color}`} />
                      <span className="text-xs font-medium text-muted-foreground">{label}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      {categoryModules.map(([key, mod]) => {
                        const Icon = mod.icon;
                        const isUsed = usedModules.includes(key);
                        return (
                          <button
                            key={key}
                            onClick={() => addModule(key)}
                            disabled={isUsed}
                            className={`flex items-center gap-2 p-2 rounded text-xs text-left ${
                              isUsed 
                                ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' 
                                : 'bg-muted hover:bg-primary/10 hover:text-primary'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span>{mod.label}</span>
                            {isUsed && <Check className="w-3 h-3 ml-auto text-green-500" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
          </div>
        </div>

        <div className="flex justify-between mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={() => { onSave(); onClose(); }}>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { activeSocieteId, activeSociete } = useWorkspace();
  const { isModuleBlocked } = usePermissions();
  const canSeeNotes = !isModuleBlocked('dashboard_notes');
  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [editMode, setEditMode] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [weather, setWeather] = useState(null);
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  
  // Titres personnalisables des widgets
  const [notesTitle, setNotesTitle] = useState('MES NOTES');
  const [tasksTitle, setTasksTitle] = useState('RAPPELS IA');
  const [proactiveTasks, setProactiveTasks] = useState([]);

  // Déterminer si c'est le dashboard d'Elsa (chevaux) ou standard
  const isElsaDashboard = activeSocieteId === 'elsa-exploitation-agricole' || 
                          activeSocieteId === 'elsarome' ||
                          activeSociete?.nom?.toLowerCase().includes('rigollier') ||
                          activeSociete?.nom?.toLowerCase().includes('elsarome');
  
  // Déterminer si c'est un domaine viticole (Emmanuel, etc.)
  const isViticoleDashboard = activeSociete?.type === 'viticulture' ||
                              activeSociete?.nom?.toLowerCase().includes('saint vincent') ||
                              activeSociete?.nom?.toLowerCase().includes('domaine');
  
  const dashboardImage = isElsaDashboard ? DASHBOARD_IMAGES.elsa : DASHBOARD_IMAGES.default;
  const dashboardAlt = isElsaDashboard ? "Bureau Centre Équestre Elsa Rigollier" : "Bureau Agricole";

  // Layout spécifique pour Elsa (sans CASIA, avec chevaux)
  const ELSA_LAYOUT = {
    etagereHaute: ['parcellaire', 'elevage', 'stocks'],
    etagereBasse: ['interventions', 'chevaux', 'planning', 'meteo'],
    tiroirsGauche: ['comptabilite', 'reglementaire', 'documents', 'carbone'],
    tiroirsDroite: ['ventes', 'clients', 'devis', 'fournisseurs'],
    postitsGauche: ['planning', 'cerveau'],
    ecran: ['cerveau', 'agents', 'elevage', 'banque'],
  };

  // Layout spécifique pour domaines viticoles (sans CASIA, sans Élevage, avec Cave)
  const VITICOLE_LAYOUT = {
    etagereHaute: ['parcellaire', 'hangar', 'stocks', 'carte'],
    etagereBasse: ['interventions', 'cave', 'planning', 'meteo'],
    tiroirsGauche: ['comptabilite', 'reglementaire', 'documents', 'carbone'],
    tiroirsDroite: ['ventes', 'clients', 'devis', 'fournisseurs'],
    postitsGauche: ['planning', 'cerveau'],
    ecran: ['cerveau', 'agents', 'cave', 'banque'],
  };

  // Charger le layout sauvegardé
  useEffect(() => {
    // Attendre que activeSociete soit chargé
    if (!activeSociete) return;
    
    // Déterminer le type de dashboard
    const isElsa = activeSocieteId === 'elsa-exploitation-agricole' || 
                   activeSocieteId === 'elsarome' ||
                   activeSociete?.nom?.toLowerCase().includes('rigollier') ||
                   activeSociete?.nom?.toLowerCase().includes('elsarome');
    
    const isViticole = activeSociete?.type === 'viticulture' ||
                       activeSociete?.nom?.toLowerCase().includes('saint vincent') ||
                       activeSociete?.nom?.toLowerCase().includes('domaine');
    
    console.log('[Dashboard] Layout check:', { isElsa, isViticole, type: activeSociete?.type, nom: activeSociete?.nom });
    
    // Pour Elsa, utiliser son layout personnalisé par défaut
    if (isElsa) {
      const savedLayout = localStorage.getItem('dashboard_layout_elsa');
      if (savedLayout) {
        try {
          setLayout(JSON.parse(savedLayout));
        } catch (e) {
          setLayout(ELSA_LAYOUT);
        }
      } else {
        setLayout(ELSA_LAYOUT);
      }
    } else if (isViticole) {
      // Pour les domaines viticoles - forcer le layout viticole
      console.log('[Dashboard] Setting VITICOLE_LAYOUT');
      setLayout(VITICOLE_LAYOUT);
    } else {
      const savedLayout = localStorage.getItem('dashboard_layout');
      if (savedLayout) {
        try {
          setLayout(JSON.parse(savedLayout));
        } catch (e) {
          console.log('Layout invalide, utilisation du défaut');
        }
      }
    }
    
    // Charger les titres personnalisés
    const savedNotesTitle = localStorage.getItem('dashboard_notes_title');
    const savedTasksTitle = localStorage.getItem('dashboard_tasks_title');
    if (savedNotesTitle) setNotesTitle(savedNotesTitle);
    if (savedTasksTitle) setTasksTitle(savedTasksTitle);
  }, [activeSociete, activeSocieteId]);

  // Charger la météo
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const response = await fetch(`${API_URL}/api/meteo/forecast?lat=44.345&lon=5.283`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setWeather(data.current);
        }
      } catch (error) {
        console.log('Météo non disponible');
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Charger les notes
  useEffect(() => {
    const savedNotes = localStorage.getItem('dashboard_notes');
    if (savedNotes) setNotes(savedNotes);
  }, []);

  const handleSaveNotes = useCallback((newNotes) => {
    setNotes(newNotes);
    localStorage.setItem('dashboard_notes', newNotes);
    toast.success('Notes sauvegardées');
  }, []);

  // Sauvegarder les titres personnalisés
  const handleNotesTitle = useCallback((newTitle) => {
    setNotesTitle(newTitle);
    localStorage.setItem('dashboard_notes_title', newTitle);
    toast.success('Titre modifié');
  }, []);

  const handleTasksTitle = useCallback((newTitle) => {
    setTasksTitle(newTitle);
    localStorage.setItem('dashboard_tasks_title', newTitle);
    toast.success('Titre modifié');
  }, []);

  const handleSaveLayout = useCallback(() => {
    localStorage.setItem('dashboard_layout', JSON.stringify(layout));
    toast.success('Bureau personnalisé sauvegardé !');
  }, [layout]);

  const handleResetLayout = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    localStorage.removeItem('dashboard_layout');
    toast.success('Bureau réinitialisé');
  }, []);

  const handleModuleClick = useCallback((route) => {
    if (!editMode) {
      navigate(route);
    }
  }, [navigate, editMode]);

  const removeFromZone = useCallback((zone, moduleKey) => {
    setLayout(prev => ({
      ...prev,
      [zone]: prev[zone].filter(m => m !== moduleKey)
    }));
  }, []);

  // Positions pour l'étagère haute
  const etagereHautePositions = [
    { top: '8%', left: '12%', width: '7%', height: '9%' },
    { top: '8%', left: '19%', width: '7%', height: '9%' },
    { top: '8%', left: '26.5%', width: '7%', height: '9%' },
    { top: '8%', left: '34%', width: '7%', height: '9%' },
  ];

  // Positions pour l'étagère basse
  const etagereBassePositions = [
    { top: '22%', left: '12%', width: '6%', height: '9%' },
    { top: '22%', left: '18%', width: '6%', height: '9%' },
    { top: '22%', left: '24%', width: '6%', height: '9%' },
    { top: '22%', left: '30%', width: '6%', height: '9%' },
    { top: '22%', left: '36%', width: '6%', height: '9%' },
  ];

  const weekTasks = proactiveTasks.length > 0 
    ? proactiveTasks.slice(0, 4).map(t => `${t.titre}${t.montant ? ` (${t.montant}€)` : ''}`)
    : ["Dites a l'assistant vos rappels", "Ex: Rappelle-moi de...", "Facture Dupont 500€"];

  // Charger les taches proactives IA
  useEffect(() => {
    const fetchProactiveTasks = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const headers = { 'Authorization': `Bearer ${token}` };
        const societeId = localStorage.getItem('societe_id') || localStorage.getItem('activeSocieteId');
        if (societeId) headers['X-Societe-Id'] = societeId;
        const res = await fetch(`${API_URL}/api/taches-proactives/pending`, { headers });
        if (res.ok) {
          const data = await res.json();
          setProactiveTasks(data.taches || []);
        }
      } catch (e) { /* silent */ }
    };
    fetchProactiveTasks();
  }, [activeSocieteId]);

  return (
    <div className="relative w-full h-screen overflow-hidden" data-testid="dashboard-page">
      {/* Image de fond - Dynamique selon l'utilisateur */}
      <img
        src={dashboardImage}
        alt={dashboardAlt}
        className="absolute inset-0 w-full h-full object-cover"
      />
      
      {/* Bouton paramètres - Plus visible */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
        <button
          onClick={() => setShowCustomization(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full shadow-lg bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 transition-all hover:scale-105"
          data-testid="customize-btn"
        >
          <Settings className="w-4 h-4" />
          <span className="text-sm font-medium">Personnaliser</span>
        </button>
      </div>

      {/* Widget Météo */}
      <div className="absolute z-40" style={{ top: '2%', right: '8%' }}>
        <MeteoWidget weather={weather} onClick={() => handleModuleClick('/meteo')} />
      </div>

      {/* Widget Briefing IA */}
      <div className="absolute z-40" style={{ top: '2%', right: '20%' }}>
        <BriefingWidget />
      </div>

      {/* Widget Alertes PAC */}
      <div className="absolute z-40" style={{ top: '2%', right: '34%' }}>
        <AlertesWidget onClick={() => handleModuleClick('/alertes-pac')} />
      </div>

      {/* Widget Veille Trouvailles */}
      <div className="absolute z-40" style={{ top: '2%', right: '48%' }}>
        <VeilleWidget />
      </div>

      {/* Widget Urgences perso (Fred only — auto-hidden si pas autorisé) */}
      <div className="absolute z-40" style={{ top: '11%', right: '8%' }}>
        <UrgencesWidget />
      </div>

      {/* Widget Hot Prospects (signaux d'achat) */}
      <div className="absolute z-40" style={{ top: '11%', right: '25%' }}>
        <HotProspectsWidget />
      </div>

      {/* Widget Mes tâches (universel - Sandrine, salariés, Fred) */}
      <div className="absolute z-40" style={{ top: '11%', left: '3%', width: '320px' }}>
        <MesTachesWidget compact={true} />
      </div>

      {/* Widget Notes - masqué si module dashboard_notes interdit */}
      {canSeeNotes && (
        <div className="absolute z-40" style={{ top: '15%', right: '3%' }}>
          <NotesWidget 
            notes={notes}
            onSave={handleSaveNotes}
            isEditing={isEditingNotes}
            setIsEditing={setIsEditingNotes}
            title={notesTitle}
            onTitleChange={handleNotesTitle}
          />
        </div>
      )}

      {/* Post-it "À ne pas oublier" */}
      <div className="absolute z-40" style={{ top: '52%', right: '3%' }}>
        <TasksPostIt 
          tasks={weekTasks}
          title={tasksTitle}
          onTitleChange={handleTasksTitle}
        />
      </div>

      {/* ÉTAGÈRE HAUTE */}
      {layout.etagereHaute?.map((moduleKey, idx) => {
        const pos = etagereHautePositions[idx];
        if (!pos) return null;
        return (
          <ClickZone
            key={`haute-${moduleKey}-${idx}`}
            moduleKey={moduleKey}
            onClick={handleModuleClick}
            style={pos}
            testId={`shelf-haute-${idx}`}
          />
        );
      })}

      {/* ÉTAGÈRE BASSE */}
      {layout.etagereBasse?.map((moduleKey, idx) => {
        const pos = etagereBassePositions[idx];
        if (!pos) return null;
        return (
          <ClickZone
            key={`basse-${moduleKey}-${idx}`}
            moduleKey={moduleKey}
            onClick={handleModuleClick}
            style={pos}
            testId={`shelf-basse-${idx}`}
          />
        );
      })}

      {/* TIROIRS DU BUREAU - Zone gauche (tiroirs du meuble) */}
      <div 
        className="absolute z-20 grid grid-rows-4 gap-1"
        style={{ top: '68%', left: '30%', width: '12%', height: '28%' }}
      >
        {layout.tiroirsGauche?.map((moduleKey) => (
          <DrawerZone 
            key={moduleKey} 
            moduleKey={moduleKey} 
            onClick={handleModuleClick}
            onRemove={(m) => removeFromZone('tiroirsGauche', m)}
            editMode={editMode}
          />
        ))}
      </div>

      {/* TIROIRS DU BUREAU - Zone droite (tiroirs du meuble) */}
      <div 
        className="absolute z-20 grid grid-rows-4 gap-1"
        style={{ top: '68%', left: '44%', width: '12%', height: '28%' }}
      >
        {layout.tiroirsDroite?.map((moduleKey) => (
          <DrawerZone 
            key={moduleKey} 
            moduleKey={moduleKey} 
            onClick={handleModuleClick}
            onRemove={(m) => removeFromZone('tiroirsDroite', m)}
            editMode={editMode}
          />
        ))}
      </div>

      {/* POST-ITS GAUCHE */}
      <div className="absolute z-30 flex flex-col gap-2" style={{ top: '3%', left: '3%' }}>
        {layout.postitsGauche?.map((moduleKey, idx) => (
          <PostIt 
            key={moduleKey} 
            moduleKey={moduleKey} 
            onClick={handleModuleClick}
            onRemove={(m) => removeFromZone('postitsGauche', m)}
            editMode={editMode}
            color={idx === 0 ? 'yellow' : 'blue'} 
          />
        ))}
      </div>

      {/* ÉCRAN D'ORDINATEUR */}
      <div 
        className="absolute z-20 flex flex-wrap items-center justify-center gap-1"
        style={{ top: '42%', left: '34%', width: '16%', height: '12%' }}
      >
        {layout.ecran?.map(moduleKey => (
          <ScreenIcon 
            key={moduleKey} 
            moduleKey={moduleKey} 
            onClick={handleModuleClick}
            onRemove={(m) => removeFromZone('ecran', m)}
            editMode={editMode}
          />
        ))}
      </div>

      {/* Widget Fil d'actualités agricoles - Icône minimale */}
      <div className="absolute z-40" style={{ bottom: '5%', left: '3%' }}>
        <ActualitesWidget />
      </div>

      {/* Panneau de personnalisation */}
      <CustomizationPanel
        open={showCustomization}
        onClose={() => setShowCustomization(false)}
        layout={layout}
        setLayout={setLayout}
        onSave={handleSaveLayout}
        onReset={handleResetLayout}
      />
    </div>
  );
}
// trigger rebuild 1778011434
